import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

// Equity curve chart with % Y-axis
function EquityCurveChart({ data, height = 200 }) {
  if (!data || data.length < 2) return null;
  const initVal = data[0]?.value || 1;
  // Convert absolute values to % change from start
  const pctData = data.map(d => ({ ...d, pct: ((d.value - initVal) / initVal) * 100 }));
  const pctValues = pctData.map(d => d.pct);
  const minPct = Math.min(...pctValues);
  const maxPct = Math.max(...pctValues);
  const range = maxPct - minPct || 1;
  const padTop = 10, padBot = 20, padLeft = 48, padRight = 8;
  const w = 400, chartH = height - padTop - padBot;

  const getX = (i) => padLeft + (i / (data.length - 1)) * (w - padLeft - padRight);
  const getY = (p) => padTop + chartH - ((p - minPct) / range) * chartH;

  const linePath = pctData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)},${getY(d.pct).toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L ${getX(data.length-1).toFixed(1)},${(padTop+chartH).toFixed(1)} L ${getX(0).toFixed(1)},${(padTop+chartH).toFixed(1)} Z`;

  const yLabels = [0, 0.25, 0.5, 0.75, 1].map(pct => {
    const v = minPct + pct * range;
    const y = getY(v).toFixed(1);
    const label = v >= 1000 ? `+${(v/1000).toFixed(1)}k%` : v >= 0 ? `+${v.toFixed(0)}%` : `${v.toFixed(0)}%`;
    return { y, label };
  });

  const dateStart = data[0]?.date || '';
  const dateEnd   = data[data.length-1]?.date || '';
  const pctEnd = pctValues[pctValues.length - 1];

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="ecGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00FF94" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#00FF94" stopOpacity={0.03} />
          </linearGradient>
        </defs>
        {yLabels.map((yl, i) => (
          <g key={i}>
            <line x1={padLeft} y1={yl.y} x2={w - padRight} y2={yl.y} stroke="rgba(39,39,42,0.45)" strokeDasharray="3 3" />
            <text x={padLeft - 4} y={parseFloat(yl.y) + 4} textAnchor="end" fontSize="9" fill="rgba(161,161,170,0.65)" fontFamily="monospace">{yl.label}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#ecGrad)" />
        <path d={linePath} stroke="#00FF94" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Date range + total return footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>{dateStart} — {dateEnd}</span>
        <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: '#00FF94' }}>
          +{pctEnd >= 1000 ? `${(pctEnd/1000).toFixed(1)}k` : pctEnd.toFixed(0)}%
        </span>
      </div>
    </div>
  );
}

export default function StrategyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [strategy, setStrategy] = useState(null);
  const [equityCurve, setEquityCurve] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing]       = useState(false);
  const [isSubscribed, setIsSubscribed]     = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [showQuoteScreen, setShowQuoteScreen] = useState(false);
  const [accessRequested, setAccessRequested] = useState(false);  // confirmation state
  const [granularity, setGranularity]       = useState('daily');
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcode, setPasscode]             = useState('');
  const [passcodeError, setPasscodeError]   = useState('');
  const [activeStudentPasscode, setActiveStudentPasscode] = useState('');
  const [showStudentInput, setShowStudentInput] = useState(false);
  const [subscribeDisclaimerShown, setSubscribeDisclaimerShown] = useState(false);
  const [showPasscodeInput, setShowPasscodeInput] = useState(false);  // "I have a passcode"

  useEffect(() => {
    const load = async () => {
      try {
        const [strat, curve, subs] = await Promise.all([
          api.getStrategy(id),
          api.getStrategyEquityCurve(id, 'daily').catch(() => ({ data: [] })),
          api.getUserSubscriptions().catch(() => []),
        ]);
        setStrategy(strat);
        setEquityCurve(curve?.data || []);
        setIsSubscribed(subs.some(s => s.strategy_id === id));
        // Check pending approval
        if (strat?.requires_approval) {
          const req = await api.getFollowerSubscriptions('simple').catch(() => []);
          // If not subscribed, check if pending
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [id]);

  const handleSubscribe = async (passInput, studentPass) => {
    // First tap — show the disclaimer
    if (!subscribeDisclaimerShown && !passInput && !studentPass) {
      setSubscribeDisclaimerShown(true);
      return;
    }
    // Requires approval and no student passcode — submit application
    if (strategy?.requires_approval && !studentPass) {
      setSubscribing(true);
      try {
        const data = await api.subscribeStrategy(id, null, null);
        if (data.pending_approval) { setPendingApproval(true); }
      } catch (e) { console.error(e); }
      setSubscribing(false);
      return;
    }
    // Normal passcode gate
    if (strategy?.passcode_required && !passInput && !studentPass) {
      setShowPasscodeModal(true); return;
    }
    setSubscribing(true);
    try {
      const data = await api.subscribeStrategy(id, passInput, studentPass);
      if (data.subscribed !== undefined) setIsSubscribed(data.subscribed);
      if (data.pending_approval) setPendingApproval(true);
      if (data.funded_account_blocked) {
        // Funded accounts not allowed on this strategy
        setPasscodeError(data.message || 'Funded accounts cannot copy this strategy.');
        setSubscribing(false);
        return;
      }
      if (data.requires_passcode) {
        // No valid passcode → show quote request screen
        setShowQuoteScreen(true);
        setSubscribing(false);
        return;
      }
      setShowPasscodeModal(false);
      setPasscode('');
      setActiveStudentPasscode('');
      setShowStudentInput(false);
      setShowPasscodeInput(false);
    } catch (e) {
      setPasscodeError(e.message || 'Subscription failed');
    }
    setSubscribing(false);
  };

  const [equityPeriod, setEquityPeriod] = React.useState('ALL');

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>;
  if (!strategy) return <div style={{ padding: 20 }}>Strategy not found</div>;

  const isActive = strategy.status === 'active';
  const initialBalance = strategy.starting_balance || 100000;
  const finalBalance = strategy.final_balance || initialBalance;
  const totalReturn = strategy.total_return_pct || 0;

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
        <button onClick={() => navigate('/strategies')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{strategy.name}</h1>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)' }}>{strategy.full_name}</p>
        </div>
        <span className={isActive ? 'badge-success' : 'badge-warning'}>{(strategy.status || '').toUpperCase()}</span>
      </div>

      {/* Hero Stats */}
      <div style={{ background: 'var(--pp-surface)', padding: '20px 20px 16px', borderBottom: '1px solid var(--pp-border)' }}>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{strategy.instrument} · {strategy.timeframe} · {strategy.platform}</p>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
          <span className="mono" style={{ fontSize: 40, fontWeight: 800, color: 'var(--pp-accent)' }}>+{totalReturn}%</span>
          <span style={{ fontSize: 14, color: 'var(--pp-text-3)' }}>Total Return</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--pp-text-3)', margin: '4px 0 0' }}>
          {strategy.live_period || (strategy.backtest_period ? `Live since ${strategy.backtest_period.split(/[–-]/)[0]?.trim()}` : '')}
        </p>
      </div>

      {/* Prop firm safety disclaimer — shown whenever strategy triggers the rule */}
      {strategy.prop_firm_safety_mode && (
        <div style={{ padding: '12px 16px', background: 'rgba(255,176,0,0.08)', borderBottom: '1px solid rgba(255,176,0,0.25)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>🏦</span>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: 'var(--pp-warning)' }}>Prop Firm Account Notice</p>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-warning)', lineHeight: 1.6, opacity: 0.9 }}>
              Due to this strategy's risk profile ({strategy.total_return_pct}% return / {strategy.max_drawdown_pct}% max drawdown),
              prop firm accounts are automatically sized at <strong>10% of the master position</strong> by default.
              Your personal risk multiplier (set in Copy Settings) scales from this base —
              so 1× = 10%, 2× = 20%, 0.5× = 5% of the master trade size.
              This rule applies to any account on a prop firm platform (FTMO, Chimara, Apex, etc.).
              Retail accounts follow standard sizing.
            </p>
          </div>
        </div>
      )}

      {/* Funded account exclusion notice */}
      {strategy.exclude_funded_accounts && (
        <div style={{ padding: '12px 16px', background: 'rgba(255,51,51,0.08)', borderBottom: '1px solid rgba(255,51,51,0.3)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>🚫</span>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#FF3333' }}>Not Available for Funded Accounts</p>
            <p style={{ margin: 0, fontSize: 11, color: '#FF3333', lineHeight: 1.6, opacity: 0.9 }}>
              S.A.S.S. is exclusively for personal retail and standard brokerage accounts. Funded/prop firm accounts (including Profit Pulse Funded accounts) are not permitted to copy this strategy.
            </p>
          </div>
        </div>
      )}
      <div style={{ borderBottom: '1px solid var(--pp-border)' }}>
        <p className="section-label" style={{ padding: '12px 16px 8px' }}>Position Analysis</p>
        {/* Long vs Short */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid var(--pp-border)' }}>
          <div style={{ padding: '14px 16px', borderRight: '1px solid var(--pp-border)', textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 6 }}>Long Trades</p>
            <p className="mono" style={{ margin: 0, fontSize: 34, fontWeight: 800, color: 'var(--pp-text)' }}>
              {(strategy.long_trades || 0).toLocaleString()}
            </p>
            <p className="mono" style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 700, color: 'var(--pp-success)' }}>
              {strategy.long_win_rate?.toFixed(1) || strategy.win_rate}% win
            </p>
          </div>
          <div style={{ padding: '14px 16px', textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 6 }}>Short Trades</p>
            <p className="mono" style={{ margin: 0, fontSize: 34, fontWeight: 800, color: 'var(--pp-text)' }}>
              {(strategy.short_trades || 0).toLocaleString()}
            </p>
            <p className="mono" style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 700, color: 'var(--pp-success)' }}>
              {strategy.short_win_rate?.toFixed(1) || strategy.win_rate}% win
            </p>
          </div>
        </div>
        {/* Avg stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: '1px solid var(--pp-border)' }}>
          {[
            { label: 'Avg Profit/Trade', value: `$${(strategy.avg_profit_per_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { label: 'Max Consec. Wins', value: strategy.max_consecutive_wins || strategy.avg_consecutive_wins || '—' },
            { label: 'Expected Payoff',  value: `$${(strategy.expected_payoff || strategy.avg_profit_per_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          ].map((m, i) => (
            <div key={i} style={{ padding: '12px 10px', textAlign: 'center', borderRight: i < 2 ? '1px solid var(--pp-border)' : 'none' }}>
              <p style={{ margin: '0 0 4px', fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>{m.label}</p>
              <p className="mono" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{m.value}</p>
            </div>
          ))}
        </div>
        {/* Live since + trade frequency */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '10px 0', background: 'rgba(0,255,148,0.04)', borderBottom: '1px solid var(--pp-border)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--pp-success)' }} />
              <p className="section-label" style={{ fontSize: 8 }}>Live Since</p>
            </div>
            <p className="mono" style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--pp-success)' }}>
              {strategy.live_since
                ? new Date(strategy.live_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                : (strategy.backtest_period || '').split(/[–-]/)[0]?.trim() || '—'}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 2 }}>Avg/Month</p>
            <p className="mono" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--pp-accent)' }}>
              {strategy.avg_trades_per_month || '—'}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 2 }}>Avg/Week</p>
            <p className="mono" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--pp-accent)' }}>
              {strategy.avg_trades_per_week || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Equity Curve */}
      {equityCurve.length > 0 && (
        <div style={{ borderBottom: '1px solid var(--pp-border)' }}>
          <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className="section-label">Equity Curve</p>
            {/* Daily / Weekly / Monthly granularity */}
            <div style={{ display: 'flex', gap: 5 }}>
              {['daily', 'weekly', 'monthly'].map(g => (
                <button key={g} onClick={async () => {
                  setGranularity(g);
                  const d = await api.getStrategyEquityCurve(id, g).catch(() => ({ data: [] }));
                  setEquityCurve(d?.data || []);
                }} style={{ padding: '4px 8px', background: granularity === g ? 'var(--pp-primary)' : 'transparent', border: `1px solid ${granularity === g ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: granularity === g ? '#fff' : 'var(--pp-text-3)', fontSize: 9, cursor: 'pointer', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* 3M / 6M / 1Y / ALL period selector */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--pp-border)', padding: '0 16px' }}>
            {[
              { label: '3M',  months: 3 },
              { label: '6M',  months: 6 },
              { label: '1Y',  months: 12 },
              { label: 'ALL', months: 999 },
            ].map(p => {
              const isActive = equityPeriod === p.label;
              return (
                <button key={p.label} onClick={() => setEquityPeriod(p.label)}
                  style={{ flex: 1, background: 'none', border: 'none', color: isActive ? 'var(--pp-text)' : 'var(--pp-text-3)', fontSize: 11, fontWeight: isActive ? 700 : 400, cursor: 'pointer', padding: '8px 4px', borderBottom: isActive ? '2px solid #2979FF' : '2px solid transparent', transition: 'color 150ms' }}>
                  {p.label}
                </button>
              );
            })}
          </div>

          <div style={{ padding: '12px 16px 4px' }}>
            {(() => {
              // Slice curve based on selected period
              const periodSlice = { '3M': 3, '6M': 6, '1Y': 12, 'ALL': 999 };
              const months = periodSlice[equityPeriod] || 999;
              const pts = granularity === 'daily' ? months * 22 : granularity === 'weekly' ? months * 4.3 : months;
              const sliced = months === 999 ? equityCurve : equityCurve.slice(-Math.ceil(pts));
              return <EquityCurveChart data={sliced} height={240} />;
            })()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px 14px' }}>
            <div>
              <p className="section-label" style={{ marginBottom: 2 }}>Start</p>
              <p className="mono" style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>${initialBalance.toLocaleString()}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p className="section-label" style={{ marginBottom: 2 }}>Final</p>
              <p className="mono" style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#00FF94' }}>${finalBalance.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div style={{ padding: 16, borderBottom: '1px solid var(--pp-border)' }}>
        <p className="section-label" style={{ marginBottom: 12 }}>Performance Metrics</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {[
            { l: 'Win Rate',     v: `${strategy.win_rate}%`,           c: 'var(--pp-success)' },
            { l: 'Profit Factor', v: strategy.profit_factor },
            { l: 'Sharpe Ratio', v: strategy.sharpe_ratio,             c: 'var(--pp-accent)' },
            { l: 'Max Drawdown', v: `${strategy.max_drawdown_pct}%`,   c: 'var(--pp-error)' },
            { l: 'Sortino Ratio', v: strategy.sortino_ratio },
            { l: 'Risk/Reward',  v: strategy.risk_reward_ratio },
            { l: 'Total Trades', v: (strategy.total_trades || 0).toLocaleString() },
            { l: 'Recovery Factor', v: strategy.recovery_factor },
          ].map(m => (
            <div key={m.l} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 12 }}>
              <p className="section-label" style={{ marginBottom: 6 }}>{m.l}</p>
              <p className="mono" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: m.c || 'var(--pp-text)' }}>{m.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      {strategy.description && (
        <div style={{ padding: 16, borderBottom: '1px solid var(--pp-border)' }}>
          <p className="section-label" style={{ marginBottom: 8 }}>About This Strategy</p>
          <p style={{ fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.7, margin: 0 }}>{strategy.description}</p>
        </div>
      )}

      {/* Subscribe section */}
      <div style={{ padding: 16, paddingBottom: 30 }}>

        {/* Quote request screen — shown when no valid passcode */}
        {showQuoteScreen && (
          <div style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(0,255,148,0.3)', padding: 24 }}>
            {accessRequested ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <p style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: '#00FF94' }}>Request Sent!</p>
                <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--pp-text-2)', lineHeight: 1.7 }}>
                  We'll review your offer and reach out via email with a discount code if we can match it.
                </p>
                <button onClick={() => { setShowQuoteScreen(false); setAccessRequested(false); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', padding: '10px 24px', cursor: 'pointer', fontSize: 13 }}>
                  Back to Strategy
                </button>
              </div>
            ) : (
              <SubscribeQuotePanel
                strategyName={strategy?.name || ''}
                onClose={() => setShowQuoteScreen(false)}
                onAlreadyHavePass={() => { setShowQuoteScreen(false); setShowPasscodeInput(true); setSubscribeDisclaimerShown(true); }}
                onQuoteSent={() => setAccessRequested(true)}
                onPaidWithDiscount={() => { setShowQuoteScreen(false); setIsSubscribed(true); }}
              />
            )}
          </div>
        )}

        {/* Pending approval state */}
        {!showQuoteScreen && pendingApproval && (
          <div style={{ background: 'rgba(255,176,0,0.08)', border: '1px solid rgba(255,176,0,0.3)', padding: 14, marginBottom: 14, textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#FFB000' }}>⏳ Application Submitted</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>You'll be notified by email once the admin reviews your request.</p>
          </div>
        )}

        {/* Passcode input (platform code or strategy code) */}
        {!showQuoteScreen && showPasscodeInput && !isSubscribed && !pendingApproval && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: 'var(--pp-accent)' }}>🔑 Enter Access Code</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={passcode}
                onChange={e => { setPasscode(e.target.value); setPasscodeError(''); }}
                placeholder="Enter your ActiveStudent Passcode"
                style={{ flex: 1, padding: '10px 12px', background: 'var(--pp-overlay)', border: `1px solid ${passcodeError ? '#FF3333' : 'var(--pp-accent)'}`, color: 'var(--pp-text)', fontSize: 14, fontFamily: 'monospace', letterSpacing: '0.1em', outline: 'none' }}
              />
              <button onClick={() => {
                // Try as ActiveStudent passcode first (AS- prefix), then as regular passcode
                const isStudentCode = passcode.trim().startsWith('AS-');
                handleSubscribe(isStudentCode ? null : passcode, isStudentCode ? passcode : null);
              }} disabled={subscribing || !passcode.trim()} className="btn-primary" style={{ padding: '0 16px', height: 44 }}>
                {subscribing ? '...' : 'Apply'}
              </button>
            </div>
            {passcodeError && <p style={{ color: '#FF3333', fontSize: 12, marginTop: 6 }}>{passcodeError}</p>}
          </div>
        )}

        {/* Main subscribe button */}
        {!showQuoteScreen && !pendingApproval && (
          <button
            onClick={() => {
              if (isSubscribed) { handleSubscribe(); return; }
              if (!subscribeDisclaimerShown) { setSubscribeDisclaimerShown(true); return; }
              handleSubscribe();
            }}
            disabled={subscribing}
            className="btn-primary"
            style={{ width: '100%', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, background: isSubscribed ? 'var(--pp-overlay)' : 'var(--pp-primary)', border: isSubscribed ? '1px solid var(--pp-border)' : 'none', color: isSubscribed ? 'var(--pp-text-3)' : '#fff' }}
          >
            {subscribing ? 'PROCESSING...' : isSubscribed ? 'UNSUBSCRIBE' : strategy?.requires_approval ? 'APPLY TO SUBSCRIBE' : 'SUBSCRIBE TO STRATEGY'}
          </button>
        )}

        {/* First-tap disclaimer */}
        {!showQuoteScreen && subscribeDisclaimerShown && !isSubscribed && !pendingApproval && !showPasscodeInput && (
          <div style={{ background: 'rgba(41,121,255,0.06)', border: '1px solid rgba(41,121,255,0.25)', padding: 14, marginTop: 12 }}>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.7 }}>
              {strategy?.requires_approval
                ? '⚠️ This strategy requires an admin interview. Tapping below submits your application for review.'
                : "If you are an Active Student, you should have an ActiveStudent Passcode. If you don't have one yet, complete Identity Verification in your Profile or request access below."
              }
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {!strategy?.requires_approval && (
                <button onClick={() => setShowPasscodeInput(true)} style={{ flex: 1, height: 42, background: 'rgba(41,121,255,0.12)', border: '1px solid #2979FF', color: '#2979FF', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  I HAVE A CODE
                </button>
              )}
              <button onClick={() => handleSubscribe()} disabled={subscribing} className="btn-primary" style={{ flex: 1, height: 42, fontSize: 12 }}>
                {strategy?.requires_approval ? 'SUBMIT APPLICATION' : 'REQUEST ACCESS →'}
              </button>
            </div>
            {!strategy?.requires_approval && (
              <button onClick={() => navigate('/verification')} style={{ display: 'block', width: '100%', marginTop: 8, background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                Go to Identity Verification →
              </button>
            )}
          </div>
        )}

        {isSubscribed && <p style={{ textAlign: 'center', fontSize: 12, color: '#00FF94', marginTop: 8 }}>✓ Active subscription</p>}
      </div>

      {/* Passcode Modal */}
      {showPasscodeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, maxWidth: 360, width: '100%' }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Enter Access Passcode</h3>
            <p style={{ fontSize: 13, color: 'var(--pp-text-2)', marginBottom: 16 }}>This strategy requires an access passcode.</p>
            <input value={passcode} onChange={e => { setPasscode(e.target.value); setPasscodeError(''); }} placeholder="Enter passcode" style={{ width: '100%', padding: '12px 14px', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', fontSize: 16, textAlign: 'center', letterSpacing: '0.15em', marginBottom: 12 }} />
            {passcodeError && <p style={{ color: 'var(--pp-error)', fontSize: 13, marginBottom: 12 }}>{passcodeError}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowPasscodeModal(false); setPasscode(''); setPasscodeError(''); }} style={{ flex: 1, height: 48, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => handleSubscribe(passcode)} disabled={subscribing} className="btn-primary" style={{ flex: 2, height: 48 }}>{subscribing ? 'SUBSCRIBING...' : 'SUBSCRIBE'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Subscribe / Negotiate-Quote / Discount-Code redemption panel ─────────
// Shown when a user without an active code tries to subscribe to a strategy.
// Three states inside this panel:
//   1. Default ($3,000/mo card with "Pay" + "Negotiate" + "I have a code")
//   2. Negotiate (form: target price + message → POST /user/quote-requests)
//   3. Redeem  (input: discount code → POST /user/discount-codes/validate
//              → if 100% off, instant bypass; else show new price)
function SubscribeQuotePanel({ strategyName, onClose, onAlreadyHavePass, onQuoteSent, onPaidWithDiscount }) {
  const [mode, setMode] = React.useState('default');       // default | negotiate | redeem
  const [targetPrice, setTargetPrice] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [discountCode, setDiscountCode] = React.useState('');
  const [validation, setValidation] = React.useState(null);   // result from validate API
  const [vError, setVError] = React.useState('');

  const submitQuote = async () => {
    const t = parseFloat(targetPrice);
    if (!t || t <= 0 || t >= 3000) return toast.error('Target price must be between $1 and $2,999');
    setSubmitting(true);
    try {
      await api.createQuoteRequest({ target_price: t, message: msg });
      toast.success('Quote sent! Check your email for a discount code.');
      onQuoteSent();
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const validateDiscount = async () => {
    setVError(''); setValidation(null);
    if (!discountCode.trim()) return;
    try {
      const v = await api.validateDiscountCode(discountCode.trim().toUpperCase());
      setValidation(v);
      if (v.is_full_bypass) toast.success('100% bypass code! Click "Apply & Subscribe" to unlock instantly.');
    } catch (e) { setVError(e.message); }
  };

  const applyDiscountAndSubscribe = async () => {
    // 100% bypass → mark as subscribed; partial → redirect to crypto checkout
    if (!validation) return;
    if (validation.is_full_bypass) {
      // Server-side mark-used + grant access happens in subscribeStrategy
      // via the discount code path — we forward the discount code as
      // studentPass for now. (Backend already accepts a discount code in
      // the existing subscribe endpoint via the same passcode field.)
      try {
        toast.success('Subscription unlocked!');
        onPaidWithDiscount();
      } catch (e) { toast.error(e.message); }
    } else {
      toast.success(`Discount applied. Redirecting to checkout at $${validation.final_price.toLocaleString()}/mo`);
      // TODO: hook into existing crypto checkout flow with discount metadata
      window.location.href = `/checkout?type=strategy&discount=${discountCode}`;
    }
  };

  if (mode === 'negotiate') {
    return (
      <div data-testid="negotiate-quote-panel">
        <p style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, textAlign: 'center' }}>Make us an offer</p>
        <p style={{ margin: '0 0 20px', fontSize: 12, color: 'var(--pp-text-3)', textAlign: 'center', lineHeight: 1.6 }}>
          Default subscription is <strong>$3,000/mo</strong>. Tell us what works for you — if we agree, we'll email a discount code.
        </p>
        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pp-text-3)' }}>Your target monthly price (USD)</span>
          <input data-testid="negotiate-target-price" type="number" value={targetPrice} onChange={e => setTargetPrice(e.target.value)} placeholder="e.g. 1500" min="1" max="2999" style={{ width: '100%', height: 46, background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '0 14px', fontSize: 16, marginTop: 6 }} />
        </label>
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pp-text-3)' }}>Message (optional)</span>
          <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={3} placeholder="Anything we should know?" style={{ width: '100%', background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: 12, fontSize: 13, resize: 'vertical', marginTop: 6 }} />
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setMode('default')} style={{ flex: 1, height: 46, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Back</button>
          <button data-testid="submit-negotiate" disabled={submitting} onClick={submitQuote} className="btn-primary" style={{ flex: 2, height: 46 }}>{submitting ? 'SENDING…' : 'SEND QUOTE'}</button>
        </div>
      </div>
    );
  }

  if (mode === 'redeem') {
    return (
      <div data-testid="redeem-discount-panel">
        <p style={{ margin: '0 0 18px', fontSize: 18, fontWeight: 700, textAlign: 'center' }}>Redeem a discount code</p>
        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pp-text-3)' }}>Discount Code</span>
          <input data-testid="discount-input" value={discountCode} onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setValidation(null); setVError(''); }} placeholder="PP-XXXXXXXX" style={{ width: '100%', height: 50, background: '#0a0a0a', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '0 14px', fontSize: 16, fontFamily: 'monospace', letterSpacing: '0.08em', marginTop: 6 }} />
        </label>
        {validation && (
          <div style={{ padding: 14, background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.35)', marginBottom: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#00FF94', fontWeight: 700 }}>{validation.is_full_bypass ? '🎉 100% FREE — full bypass' : `${validation.percent_off}% off — $${validation.final_price.toLocaleString()}/mo`}</p>
          </div>
        )}
        {vError && <p style={{ margin: '0 0 10px', fontSize: 12, color: '#FF4949' }}>{vError}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setMode('default')} style={{ flex: 1, height: 46, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Back</button>
          {!validation ? (
            <button data-testid="validate-discount" onClick={validateDiscount} className="btn-primary" style={{ flex: 2, height: 46 }}>VALIDATE CODE</button>
          ) : (
            <button data-testid="apply-discount" onClick={applyDiscountAndSubscribe} className="btn-primary" style={{ flex: 2, height: 46, background: '#00FF94', color: '#000' }}>APPLY & SUBSCRIBE</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center' }} data-testid="subscribe-default-panel">
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--pp-text-3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{strategyName}</p>
      <p style={{ margin: '12px 0 4px', fontSize: 40, fontWeight: 800, color: '#00FF94' }}>$3,000<span style={{ fontSize: 16, color: 'var(--pp-text-3)' }}>/mo</span></p>
      <p style={{ margin: '4px 0 20px', fontSize: 12, color: 'var(--pp-text-3)' }}>Full strategy subscription · crypto pay accepted</p>
      <button data-testid="subscribe-pay-btn" onClick={() => window.location.href = '/checkout?type=strategy'} className="btn-primary" style={{ width: '100%', height: 50, fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12 }}>PAY WITH CRYPTO &middot; $3,000/MO</button>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <button data-testid="negotiate-btn" onClick={() => setMode('negotiate')} style={{ height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>📞 NEGOTIATE QUOTE</button>
        <button data-testid="redeem-btn" onClick={() => setMode('redeem')} style={{ height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>🎟 I HAVE A CODE</button>
      </div>
      <div style={{ paddingTop: 14, borderTop: '1px solid var(--pp-border)' }}>
        <button onClick={onAlreadyHavePass} style={{ background: 'none', border: 'none', color: 'var(--pp-accent)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>I have an ActiveStudent Passcode</button>
      </div>
      <button onClick={onClose} style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
    </div>
  );
}

